/* Visits — the approval list.
   Visual reference: docs/design/visits.html. Normative rules: docs/design/spec/.

   Shape (spec/03 archetype A): page head → filter card → status segment → table → sticky bulk bar.
   Filters apply as they change. Everything narrows on the SERVER — the page never filters or
   sorts rows it already has, because it only ever holds one page of them.

   The endpoint this runs on gained the filters it needed in the same change (picsell-api
   `visits/api/web/v1/filters.py`): `county` and `supervisor` were being sent by the old page and
   silently dropped, and `status` / multi-value `users` / `networks` / `points` did not exist.

   Two columns the design draws as sortable are not: report status and progress are computed per
   visit outside the queryset (`collect_status_reports_bulk`), so ordering by them could only ever
   sort the loaded page — which reads as a sort and is not one. */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Card, Checkbox, Col, Dropdown, Empty, Flex, Form, Progress, Row, Segmented, Space, Table, Tooltip, Typography } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ClusterOutlined,
  CompassOutlined,
  ExportOutlined,
  IdcardOutlined,
  PrinterOutlined,
  PushpinOutlined,
  TableOutlined,
  TeamOutlined,
} from "@ant-design/icons";

import Alert from "/src/util/alert";

import EntitySelect from "../../component/EntitySelect";
import FilterChips, { useLabelCache } from "../../component/FilterChips";
import { PersonCell, RowActions, StatusIcon, TableFoot, actionsColumn, statusColumn, useTableScrollY } from "../../component/table-cells";
import PeriodPicker, { clampRange, defaultRange } from "../../component/PeriodPicker";
import { ENTITY_DROPDOWN_SETTINGS, PHOTO_REPORT, SIDE_NAV, TRANSLATION_DOWNLOADER, VISIT_PAGE } from "../../config/lang";
import { call, getAbortable, isOk } from "../../util/action";
import dayjs from "../../util/dayjs";
import { formatDuration } from "../../util/duration";
import { fill, regionId, translate } from "../../util/util";
import { localeFor } from "../merchandiser/route/dateUtils";

import "./visits.scss";

const PAGE_SIZES = [10, 20, 50, 100];

/** Widest period the list will accept, in DAYS — the same cap `/task`, `/photo-review` and
    `/uploads` enforce, so every period control in the app offers the same six presets.

    The DEFAULT is the shared `defaultRange()` — two weeks, today inclusive — not the cap. It had
    no cap at all before: a pasted URL could ask for a year of visits and `VisitFilter` would honour
    it, and the period is the only thing bounding how many rows the endpoint considers. It opened on
    a single day for a while, which kept the first paint cheap but put every visit older than this
    morning a filter change away; two weeks is what `/answers` opens on too.

    Held in three places, as `/answers` holds its own: the calendar greys out days beyond the cap,
    `searchToState` clamps a pasted URL, and `buildQuery` clamps whatever is in state at request
    time — the last is what actually holds, including for `/visit/:id`'s queue stepping, which
    re-runs `buildQuery`. */
const MAX_RANGE_DAYS = 90;

const STATUS_SEGMENTS = [
  ["all", "status_all"],
  ["pending", "status_pending"],
  ["approved", "status_approved"],
  ["rejected", "status_rejected"],
];

/* `is_approved` is a nullable boolean: null means still on verification. */
const statusOf = visit => (visit?.is_approved === true ? "approved" : visit?.is_approved === false ? "rejected" : "pending");

const STATUS_ICON = {
  approved: { Icon: CheckCircleOutlined, tone: "ok", key: "approved_single_visit_name" },
  rejected: { Icon: CloseCircleOutlined, tone: "no", key: "no_approved_single_visit_name" },
  pending: { Icon: ClockCircleOutlined, tone: "wait", key: "on_verification_single_visit_name" },
};

/* Column key → the field the API orders on. A key missing here is not sortable (see the header
   comment); `duration` is an annotation on the queryset, not a column. */
const ORDERING = {
  point: "point__name",
  approved_by: "approved_by__first_name",
  author: "user__first_name",
  time: "duration",
};

/* Six tracks with the period spanning two — seven units, and 24 does not divide by seven. A
   `<Col>`'s responsive value may be an object rather than a span, and that object takes `flex`,
   so the track is a percentage instead. `0 0` rather than `1 1`: a short last row leaves empty
   tracks instead of stretching the fields it does have. */
const FIELD_COL = { xs: { flex: "0 0 100%" }, sm: { flex: "0 0 50%" }, md: { flex: "0 0 33.3333%" }, xl: { flex: "0 0 14.2857%" } };
const PERIOD_COL = { xs: { flex: "0 0 100%" }, sm: { flex: "0 0 100%" }, md: { flex: "0 0 66.6667%" }, xl: { flex: "0 0 28.5714%" } };

const blankFilter = () => ({ county: "", network: "", users: [], supervisor: [], points: [] });

const SINGLE_KEYS = ["county", "network"];
const ARRAY_KEYS = ["users", "supervisor", "points"];

/* Which ENTITY_DROPDOWN_SETTINGS key names each filter's chip. */
const CHIP_LABEL = { county: "region", network: "network", users: "merchandisers", supervisor: "supervisor", points: "points" };

/** Which VISIT_PAGE key names each toggleable column in the Columns panel. */
const COLUMN_LABEL = {
  supervisor: "supervisor",
  approved_by: "approved_by",
  author: "author_way",
  time: "table_timespent",
  reports: "status_label",
  progress: "progress_label",
};
const TOGGLEABLE = Object.keys(COLUMN_LABEL);

const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);
const joinIds = list =>
  (list || [])
    .map(idOf)
    .filter(v => v !== undefined && v !== null && v !== "")
    .join(",");

const isoDay = d => dayjs(d).format("YYYY-MM-DD");
const fullName = user => (user ? [user.first_name, user.last_name].filter(Boolean).join(" ") : "");

/**
 * The request the table runs on.
 *
 * Built here rather than through `filterUrl()` for the same reason the photo report builds its
 * own: the per-field shape the endpoint expects is then visible in one place. The period matches
 * on the visit's START (`start__gte` / `start__lte`) — a visit that is still open has no `end`,
 * and filtering on `end__lte` dropped exactly the rows a supervisor is most likely looking for.
 */
export function buildQuery({ filter, range, status, page, size, sort }) {
  // The backstop. Every other clamp is convenience; this one is on the path to the request.
  const period = clampRange(range, MAX_RANGE_DAYS);
  const p = new URLSearchParams();
  p.set("page", String(page));
  p.set("page_size", String(size));

  if (filter.county) p.set("county", String(idOf(filter.county)));
  if (filter.network) p.set("networks", String(idOf(filter.network)));
  if (filter.users?.length) p.set("users", joinIds(filter.users));
  if (filter.supervisor?.length) p.set("supervisor", joinIds(filter.supervisor));
  if (filter.points?.length) p.set("points", joinIds(filter.points));
  if (status !== "all") p.set("status", status);
  if (period?.[0]) p.set("start__gte", isoDay(period[0]));
  /* Inclusive end: `start__lte` against a date is midnight, so the picked day itself would be
     excluded for every visit that did not begin at 00:00. */
  if (period?.[1]) p.set("start__lte", isoDay(dayjs(period[1]).add(1, "day")));
  if (sort) p.set("ordering", `${sort.dir === "descend" ? "-" : ""}${ORDERING[sort.key]}`);

  return p.toString();
}

/** URL ↔ state sync, so a filtered view can be shared, bookmarked, and survives coming back
    from a visit's detail page with the browser's Back button. */
function stateToSearch({ filter, range, status, page, size, sort }) {
  const p = new URLSearchParams();
  if (range?.[0]) p.set("start", isoDay(range[0]));
  if (range?.[1]) p.set("end", isoDay(range[1]));
  if (filter.county) p.set("county", String(idOf(filter.county)));
  if (filter.network) p.set("network", String(idOf(filter.network)));
  (filter.users || []).forEach(v => p.append("users", String(idOf(v))));
  (filter.supervisor || []).forEach(v => p.append("supervisor", String(idOf(v))));
  (filter.points || []).forEach(v => p.append("points", String(idOf(v))));
  if (status !== "all") p.set("status", status);
  if (page > 1) p.set("page", String(page));
  if (size !== PAGE_SIZES[0]) p.set("size", String(size));
  if (sort) p.set("sort", `${sort.dir === "descend" ? "-" : ""}${sort.key}`);
  return p.toString();
}

export function searchToState(search, me) {
  const p = new URLSearchParams(search);
  const filter = blankFilter();
  if (p.get("county")) filter.county = p.get("county");
  if (p.get("network")) filter.network = p.get("network");
  /* Ids only — `EntitySelect` names them once it has fetched the option they belong to. */
  filter.users = p.getAll("users").map(v => ({ id: Number(v) }));
  filter.supervisor = p.getAll("supervisor").map(v => ({ id: Number(v) }));
  filter.points = p.getAll("points").map(v => ({ id: Number(v) }));

  const hasFilters = Boolean(search && search.replace(/^\?/, ""));
  /* Without a URL to restore, the supervisor's own region is the sensible default view — it is
     the scope they are responsible for, and the old page seeded it the same way. */
  if (!hasFilters) filter.county = regionId(me) || "";

  const start = p.get("start");
  const end = p.get("end");
  const sort = p.get("sort");
  return {
    filter,
    /* A shared URL with filters but no dates used to arrive with NO window — the one path that
       could ask for everything. It gets the default like any other. */
    range: start && end ? clampRange([dayjs(start), dayjs(end)], MAX_RANGE_DAYS) : defaultRange(),
    status: STATUS_SEGMENTS.some(([value]) => value === p.get("status")) ? p.get("status") : "all",
    page: Math.max(1, Number(p.get("page")) || 1),
    size: PAGE_SIZES.includes(Number(p.get("size"))) ? Number(p.get("size")) : PAGE_SIZES[0],
    sort: sort && ORDERING[sort.replace(/^-/, "")] ? { key: sort.replace(/^-/, ""), dir: sort[0] === "-" ? "descend" : "ascend" } : null,
  };
}

/* ------------------------------------------------------------------ cells */

/** Completed / on review / not completed, as one bar — `collect_status_reports` from the API.
    The counts sit ON the fill, so the fill is a `-700` and not the `-500` the mockup draws:
    white on `--success-500` is 4.21:1 and text needs 4.5:1 (spec/01#status-colour). */
function ReportBar({ reports, lang }) {
  const parts = [
    [reports?.completed_reports || 0, "ok", "approved_label_popup"],
    [reports?.on_review_reports || 0, "wait", "pending_label_popup"],
    [reports?.not_completed_reports || 0, "no", "rejected_label_popup"],
  ];
  const total = parts.reduce((sum, [n]) => sum + n, 0);
  if (!total) return <span className="vt-bar vt-bar--empty" aria-hidden="true" />;
  return (
    <span className="vt-bar">
      {parts.map(([n, tone, key]) =>
        n ? (
          <Tooltip key={tone} title={`${translate(VISIT_PAGE, key, lang)}: ${n}`}>
            <span className={`vt-bar__seg vt-bar__seg--${tone}`} style={{ flex: `${n} 1 0` }}>
              {n}
            </span>
          </Tooltip>
        ) : null
      )}
    </span>
  );
}

/* ------------------------------------------------------------------ page */

const List = () => {
  const history = useHistory();
  const location = useLocation();
  const me = useSelector(state => state.me);
  const lang = me?.language || "en";

  const t = key => translate(VISIT_PAGE, key, lang);
  const td = key => translate(ENTITY_DROPDOWN_SETTINGS, key, lang);
  const tp = key => translate(PHOTO_REPORT, key, lang);

  /* Read once. After mount this component owns the state and writes the URL, not the reverse —
     reading it back on every change would fight the user's own typing. */
  const initial = useRef(searchToState(location.search, me)).current;

  const [filter, setFilter] = useState(initial.filter);
  const [range, setRangeState] = useState(initial.range);
  /* Everything routes through here — picker, chip ×, Reset — so "the state never holds a window
     wider than the cap, and never holds none" is an invariant rather than a per-call-site rule. */
  const setRange = next => setRangeState(clampRange(next, MAX_RANGE_DAYS) || defaultRange());
  const [status, setStatus] = useState(initial.status);
  const [page, setPage] = useState(initial.page);
  const [size, setSize] = useState(initial.size);
  const [sort, setSort] = useState(initial.sort);

  /* The option objects the selects handed back, so a chosen value labels itself. */
  const [chosen, setChosen] = useState({ county: null, network: null });

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState([]);

  /* --------------------------------------------------------------- fetch */

  const query = useMemo(() => buildQuery({ filter, range, status, page, size, sort }), [filter, range, status, page, size, sort]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    getAbortable(`visits/?${query}`, controller.signal)
      .then(response => {
        if (!response) return;
        setRows(response.results || []);
        setTotal(response.count || 0);
        setLoading(false);
      })
      .catch(error => {
        if (error.name === "AbortError") return;
        setRows([]);
        setTotal(0);
        setLoading(false);
      });
    return () => controller.abort();
  }, [query]);

  /* The URL trails the state; `replace` so filtering does not fill the history stack, and the
     Back button still leaves the page rather than undoing one filter at a time. */
  useEffect(() => {
    const search = stateToSearch({ filter, range, status, page, size, sort });
    if (search !== location.search.replace(/^\?/, "")) history.replace({ search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, range, status, page, size, sort]);

  /* Any narrowing invalidates the page number: page 7 of the old result set is usually past the
     end of the new one, which would render an empty table over a non-empty result. */
  const narrow = fn => (...args) => {
    setPage(1);
    setSelected([]);
    fn(...args);
  };

  const setSingle = narrow((key, option) => {
    setChosen(prev => ({ ...prev, [key]: option }));
    setFilter(prev => ({ ...prev, [key]: option ? idOf(option) : "" }));
  });
  const setArray = narrow((key, list) => setFilter(prev => ({ ...prev, [key]: list })));

  const resetFilters = narrow(() => {
    setFilter(blankFilter());
    setChosen({ county: null, network: null });
    setRange(defaultRange());
    setStatus("all");
    setSort(null);
  });

  /* ---------------------------------------------------------------- chips */

  const { labels, remember, nameFor } = useLabelCache();

  const clearChip = narrow((key, id) => {
    if (key === "period") {
      setRange(defaultRange());
      return;
    }
    if (SINGLE_KEYS.includes(key)) {
      setChosen(prev => ({ ...prev, [key]: null }));
      setFilter(prev => ({ ...prev, [key]: "" }));
      return;
    }
    setFilter(prev => ({ ...prev, [key]: (prev[key] || []).filter(v => String(idOf(v)) !== String(id)) }));
  });

  const chips = useMemo(() => {
    const out = [];
    if (range?.[0] && range?.[1]) {
      /* Short form, per spec/04 — `28 Jul → 11 Aug`. The year shows only when the range leaves
         the current one, which is also what keeps the chip on one line on a phone. */
      const thisYear = new Date().getFullYear();
      const sameYear = range[0].year() === thisYear && range[1].year() === thisYear;
      const fmt = new Intl.DateTimeFormat(localeFor(lang), { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
      out.push({ key: "period", label: tp("period"), value: `${fmt.format(range[0].toDate())} → ${fmt.format(range[1].toDate())}` });
    }
    SINGLE_KEYS.forEach(key => {
      if (!filter[key]) return;
      /* `chosen[key]` is the option object the select handed back, so it names itself; falling
         back to the bare id sends `nameFor` to the resolution cache. */
      out.push({ key, label: td(CHIP_LABEL[key]), value: nameFor(key, chosen[key] ?? filter[key]) });
    });
    ARRAY_KEYS.forEach(key => {
      (filter[key] || []).forEach(v => out.push({ key, id: idOf(v), label: td(CHIP_LABEL[key]), value: nameFor(key, v) }));
    });
    return out;
    /* `labels` is a dependency: a chip that arrived as an id has to re-render once its name lands. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, chosen, labels, range, lang]);

  /* ------------------------------------------------------- approve / reject */

  const patchVisit = (visit, value) =>
    new Promise(resolve => {
      /* `isOk`, not truthiness — a 5xx hands the callback a truthy failedResponse (see action.js). */
      call({ method: "PATCH", url: `visits/${visit.id}/`, post: { is_approved: value, approved_by: me.id } }, response => resolve(isOk(response)));
    });

  /* The row is patched in place rather than refetched, so the table does not jump under the
     cursor. A row that no longer matches the active status filter stays until the next fetch: its
     dot has already changed, so it is showing the result of the action, not a stale state. */
  const applyLocally = (ids, value) => {
    setRows(prev => prev.map(row => (ids.includes(row.id) ? { ...row, is_approved: value, approved_by: me } : row)));
  };

  const decide = async (visit, value) => {
    if (visit.is_approved === value) {
      Alert.warning(t(value ? "already_approve_visit_msg" : "already_reject_visit_msg"), { position: "top-right" });
      return;
    }
    setBusy(true);
    const ok = await patchVisit(visit, value);
    setBusy(false);
    if (!ok) return;
    applyLocally([visit.id], value);
    Alert.success(t(value ? "approve_visit_msg" : "reject_visit_msg"), { position: "top-right" });
  };

  const decideSelected = async value => {
    const targets = rows.filter(row => selected.includes(row.id) && row.is_approved !== value);
    if (!targets.length) {
      setSelected([]);
      return;
    }
    setBusy(true);
    const results = await Promise.all(targets.map(row => patchVisit(row, value)));
    setBusy(false);
    const done = targets.filter((row, i) => results[i]);
    if (done.length) {
      applyLocally(
        done.map(row => row.id),
        value
      );
      Alert.success(fill(t(value ? "approve_visits_msg" : "reject_visits_msg"), { n: done.length }), { position: "top-right" });
    }
    if (done.length < targets.length) {
      Alert.warning(fill(t("decide_failed_msg"), { n: targets.length - done.length }), { position: "top-right" });
    }
    setSelected([]);
  };

  /* Walk into the review workspace carrying this row's position in the FILTERED list, so the
     detail page's prev/next stepping continues the same queue. The filters ride along in the
     same query string, which is what lets it ask for the neighbour without re-deriving them. */
  const openVisit = (visit, indexOnPage) => {
    const search = new URLSearchParams(stateToSearch({ filter, range, status, page, size, sort }));
    if (indexOnPage != null) {
      search.set("i", String((page - 1) * size + indexOnPage + 1));
      search.set("n", String(total));
    }
    history.push(`/visit/${visit.id}?${search.toString()}`);
  };

  /* ------------------------------------------------------------- columns */

  const dateFmt = useMemo(() => new Intl.DateTimeFormat(localeFor(lang), { day: "2-digit", month: "2-digit", year: "numeric" }), [lang]);

  const columns = useMemo(() => {
    const all = [
      statusColumn((value, row) => {
        const { Icon, tone, key } = STATUS_ICON[statusOf(row)];
        return <StatusIcon Icon={Icon} tone={tone} label={t(key)} />;
      }),
      {
        key: "point",
        title: td("point"),
        width: 300,
        fixed: "left",
        ellipsis: true,
        sorter: true,
        render: (value, row) => {
          const title = [row.point?.locality?.name, row.point?.name].filter(Boolean).join(" · ");
          const meta = [row.point?.address, row.start ? dateFmt.format(new Date(row.start)) : null].filter(Boolean).join(" · ");
          return (
            <Flex vertical className="lp-main">
              <Typography.Text strong ellipsis={{ tooltip: title }}>
                {title}
              </Typography.Text>
              <Typography.Text type="secondary" className="lp-main__sub" ellipsis={{ tooltip: meta }}>
                {meta}
              </Typography.Text>
            </Flex>
          );
        },
      },
      {
        key: "supervisor",
        title: t("supervisor"),
        width: 190,
        ellipsis: true,
        /* The point's responsible supervisors are an M2M; ordering across it duplicates rows,
           so this column shows the first one and does not sort. */
        render: (value, row) => <PersonCell user={row.point?.responsible_supervisors?.[0]} strong={false} />,
      },
      {
        key: "approved_by",
        title: t("approved_by"),
        width: 190,
        ellipsis: true,
        sorter: true,
        render: (value, row) => <PersonCell user={row.approved_by} strong={false} />,
      },
      {
        key: "author",
        title: t("author_way"),
        width: 200,
        ellipsis: true,
        sorter: true,
        render: (value, row) => (
          <Flex vertical className="lp-main">
            <Typography.Text ellipsis={{ tooltip: fullName(row.user) }}>{fullName(row.user) || "—"}</Typography.Text>
            <Typography.Text type="secondary" className="lp-main__sub">
              {t(row.is_planned ? "on_route" : "off_route")}
            </Typography.Text>
          </Flex>
        ),
      },
      {
        key: "time",
        title: t("table_timespent"),
        width: 110,
        align: "right",
        sorter: true,
        render: (value, row) => {
          const spent = formatDuration(row.time_spent, lang);
          return spent ? <span className="lp-num">{spent}</span> : <span className="lp-dash">—</span>;
        },
      },
      {
        key: "reports",
        title: t("status_label"),
        width: 150,
        render: (value, row) => <ReportBar reports={row.collect_status_reports} lang={lang} />,
      },
      {
        key: "progress",
        title: t("progress_label"),
        width: 130,
        align: "center",
        render: (value, row) => {
          const percent = row.collect_status_reports?.progress_reports || 0;
          return (
            <Flex align="center" justify="center" gap={8} className="vt-prog">
              <Progress percent={percent} size={[46, 6]} showInfo={false} aria-hidden="true" />
              <span className="lp-num">{percent}%</span>
            </Flex>
          );
        },
      },
      actionsColumn((value, row, index) => (
        <RowActions
          label={t("row_actions")}
          items={[
            { key: "open", icon: <ExportOutlined />, label: t("open_visit") },
            { key: "approve", icon: <CheckCircleOutlined />, label: t("approv_btn") },
            { key: "reject", icon: <CloseCircleOutlined />, label: t("reject_btn"), danger: true },
          ]}
          onClick={key => (key === "open" ? openVisit(row, index) : decide(row, key === "approve"))}
        />
      )),
    ];
    return all.filter(column => !hidden.includes(column.key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, hidden, rows, dateFmt]);

  const scrollY = useTableScrollY();

  return (
    <Flex vertical gap="middle" className="shell-content lp vt">
      <Flex className="lp-head" justify="space-between" align="flex-end" gap="middle" wrap>
        <Flex vertical>
          <Typography.Title level={1} className="lp-title">
            {translate(SIDE_NAV, "visit", lang)}
          </Typography.Title>
          <Typography.Paragraph type="secondary" className="lp-sub">
            {t("page_sub")}
          </Typography.Paragraph>
        </Flex>
        <Tooltip title={tp("print_hint")}>
          <Button icon={<PrinterOutlined />} onClick={() => window.print()}>
            {translate(TRANSLATION_DOWNLOADER, "export", lang)}
          </Button>
        </Tooltip>
      </Flex>

      <Card className="lp-filters" variant="outlined">
        {/* `component={false}`: there is nothing to submit — filters apply as they change, and a
            real <form> would turn Enter in the search box into a page reload. */}
        <Form layout="vertical" component={false} colon={false}>
          <Row gutter={[12, 12]}>
            <Col {...PERIOD_COL}>
              <Form.Item label={tp("period")}>
                <PeriodPicker value={range} onChange={narrow(setRange)} maxDays={MAX_RANGE_DAYS} clearTo={defaultRange} />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={td("region")}>
                <EntitySelect
                  entity="county"
                  value={filter.county}
                  seed={chosen.county || (me?.county && typeof me.county === "object" ? me.county : null)}
                  prefix={<CompassOutlined />}
                  onChange={option => setSingle("county", option)}
                  onResolve={remember("county")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={td("network")}>
                <EntitySelect
                  entity="network"
                  value={filter.network}
                  seed={chosen.network}
                  prefix={<ClusterOutlined />}
                  onChange={option => setSingle("network", option)}
                  onResolve={remember("network")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={td("merchandisers")}>
                <EntitySelect
                  entity="merchandisers"
                  isMulti
                  value={filter.users}
                  prefix={<IdcardOutlined />}
                  onChange={list => setArray("users", list)}
                  onResolve={remember("users")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={td("supervisor")}>
                <EntitySelect
                  entity="supervisor"
                  isMulti
                  value={filter.supervisor}
                  prefix={<TeamOutlined />}
                  onChange={list => setArray("supervisor", list)}
                  onResolve={remember("supervisor")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              {/* Scoped by the two filters beside it — `EntitySelect` sends `locality__county` and
                  `network`, so the list offers the points the rest of the filter row implies. A
                  scope change drops the loaded options; values already chosen keep their labels. */}
              <Form.Item label={td("points")}>
                <EntitySelect
                  entity="points"
                  isMulti
                  value={filter.points}
                  county={filter.county}
                  moduleFilter={filter.network}
                  prefix={<PushpinOutlined />}
                  onChange={list => setArray("points", list)}
                  onResolve={remember("points")}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* What is actually applied, in words — the selects themselves collapse to `+N` once a
              filter holds more than one value, so without this row the only way to read the
              current query is to open each dropdown. */}
          <FilterChips className="lp-chips" items={chips} closeLabel={tp("a_remove_filter")} onClose={clearChip} />

          <Flex className="lp-filters__foot" align="center" gap="small" wrap>
            <Segmented value={status} onChange={narrow(setStatus)} options={STATUS_SEGMENTS.map(([value, key]) => ({ value, label: tp(key) }))} />
            <span className="lp-spacer" />
            <Button size="small" onClick={resetFilters}>
              {td("filter_reset")}
            </Button>
          </Flex>
        </Form>
      </Card>

      <Card className="lp-table" variant="outlined">
        <Flex className="lp-table__bar" align="center" gap="small" wrap>
          <Typography.Text strong className="lp-table__title">
            {translate(SIDE_NAV, "visit", lang)}
          </Typography.Text>
          <Typography.Text type="secondary" aria-live="polite">
            {tp("records_count")}: <Typography.Text strong>{total}</Typography.Text>
          </Typography.Text>
          <span className="lp-spacer" />
          <Dropdown
            trigger={["click"]}
            /* A plain checkbox list, not a `menu` — a menu item swallows the checkbox's own
               click and closes the panel after every single toggle. */
            popupRender={() => (
              <Card className="vt-colcfg" variant="outlined" size="small">
                <Checkbox.Group
                  value={TOGGLEABLE.filter(key => !hidden.includes(key))}
                  onChange={visible => setHidden(TOGGLEABLE.filter(key => !visible.includes(key)))}>
                  <Flex vertical gap={4}>
                    {TOGGLEABLE.map(key => (
                      <Checkbox key={key} value={key}>
                        {t(COLUMN_LABEL[key])}
                      </Checkbox>
                    ))}
                  </Flex>
                </Checkbox.Group>
              </Card>
            )}>
            <Button size="small" icon={<TableOutlined />}>
              {t("columns")}
            </Button>
          </Dropdown>
        </Flex>

        <Table
          className="lp-table__grid"
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={rows}
          loading={loading}
          scroll={{ x: 1420, y: scrollY }}
          rowClassName={row => (selected.includes(row.id) ? "lp-row lp-row--on" : "lp-row")}
          rowSelection={{
            fixed: true,
            selectedRowKeys: selected,
            onChange: keys => setSelected(keys),
          }}
          onRow={(row, index) => ({
            onClick: event => {
              /* The selection cell and the row menu own their clicks; everything else walks
                 into the visit. */
              if (event.target.closest(".ant-table-selection-column, .ant-dropdown, .ant-btn")) return;
              openVisit(row, index);
            },
          })}
          /* Paging is the footer below, so the only thing left for the table's own onChange is
             the sorter — with `pagination={false}` its first argument carries nothing. */
          onChange={(pagination, filters, sorter) => {
            const next = sorter?.order && ORDERING[sorter.columnKey] ? { key: sorter.columnKey, dir: sorter.order } : null;
            if (JSON.stringify(next) === JSON.stringify(sort)) return;
            setSort(next);
            setPage(1);
          }}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Flex vertical gap={4}>
                    <Typography.Text strong>{t("empty_title")}</Typography.Text>
                    <Typography.Text type="secondary">{t("empty_desc")}</Typography.Text>
                  </Flex>
                }
              />
            ),
          }}
        />

        {/* The design's footer: rows-per-page on the left, the range and the pager on the right.
            Shared with every other redesigned table — see `component/table-cells.js`. */}
        <TableFoot
          id="vt"
          page={page}
          size={size}
          total={total}
          sizes={PAGE_SIZES}
          labels={{ perPage: t("rows_per_page"), range: t("range_of") }}
          onPage={next => {
            setPage(next);
            setSelected([]);
          }}
          onSize={next => {
            setSize(next);
            setPage(1);
            setSelected([]);
          }}
        />
      </Card>

      {selected.length > 0 && (
        <Flex className="lp-bulkbar" align="center" gap="middle" wrap>
          <Typography.Text className="lp-bulkbar__n">{fill(tp("w_selected"), { n: selected.length })}</Typography.Text>
          <Space className="lp-bulkbar__acts" size={8}>
            <Button size="small" onClick={() => setSelected([])}>
              {tp("clear_selection")}
            </Button>
            <Button size="small" className="vt-btn--ok" icon={<CheckCircleOutlined />} loading={busy} onClick={() => decideSelected(true)}>
              {t("approv_btn")}
            </Button>
            <Button size="small" danger type="primary" icon={<CloseCircleOutlined />} loading={busy} onClick={() => decideSelected(false)}>
              {t("reject_btn")}
            </Button>
          </Space>
        </Flex>
      )}
    </Flex>
  );
};

export default List;
/* `EntityTable`'s `entity === "visit"` branches — including the `visitCurrentPage` /
   `visitCurrentFilter` localStorage round-trip this page used to need — have no caller left.
   They are dead, not load-bearing; they go with the next legacy sweep, along with
   `formatter.js`'s visit cell helpers. */
